/**
 * @name Employee.js
 * @author 양진영
 * @since 2025. 02. 26.
 * @description
 * 사원 관리(조회, 등록, 수정, 삭제)
 */

import React, { useEffect, useState, useContext, useRef } from "react";
import { FormProvider } from "react-hook-form";
import { Input, Select, Pagination, SortHeader, CompanySearchModal } from "@components";
import { useEmployeeList } from "@hooks";
import { get, post } from "@utils/axios_api";
import { AuthContext, useAdminCompany } from "@context";
import { ROLES } from "@constants/roles";
import { toast } from 'react-toastify';

const Employee = () => {

  // AuthContext에서 로그인 정보(여기서는 compCode)를 추출합니다.
  const { auth, hasPermission } = useContext(AuthContext);
  const canWrite = hasPermission('EMPLOYEE_WRITE');
  const canDelete = hasPermission('EMPLOYEE_DELETE');
  const isAdmin = auth.role === ROLES.SUPER_ADMIN || auth.role === ROLES.ADMIN;

  const ALL_ROLE_OPTIONS = [
    { value: 'R0', label: 'R0 (최고관리자)' },
    { value: 'R1', label: 'R1 (일반관리자)' },
    { value: 'R2', label: 'R2 (회사관리자)' },
    { value: 'R3', label: 'R3 (일반사용자)' },
  ];
  const assignableRoles = ALL_ROLE_OPTIONS.filter(r => {
    const loginRole = auth?.roleId;
    if (loginRole === 'R0') return true;
    if (loginRole === 'R1') return ['R1', 'R2', 'R3'].includes(r.value);
    if (loginRole === 'R2') return ['R2', 'R3'].includes(r.value);
    return false;
  });
  // 전역 선택한 회사 번호 (R0 슈퍼관리자)
  const { adminCompCode, adminCompName, setAdminCompCode, setAdminCompName } = useAdminCompany();

  const compCode = isAdmin ? adminCompCode : auth?.compCode;

  const {
    methods, data, companyData, isLoading, isError, error, selectedItems, isAllChecked, onSubmit,
    handlePageChange, changePageSize, handleSelectAll, handleSelectItem, handleDelete, refetch, initialEmployee,
    pageIndex, recordCountPerPage
  } = useEmployeeList(compCode);

  const [modalOpen, setModalOpen] = useState(false);
  const [selectEmployee, setSelectEmployee] = useState(initialEmployee);
  const [siteList, setSiteList] = useState([]);
  const [filterDeptList, setFilterDeptList] = useState([]);
  const [filterPosList, setFilterPosList] = useState([]);
  const empNameInputRef = useRef(null);

  const [filterNoAccount, setFilterNoAccount] = useState(false);
  const [bulkAccountModal, setBulkAccountModal] = useState({ open: false, successCount: 0, failList: [], results: [] });

  // 콤보박스 데이터 조회 (회사별 사업장/부서/직위 목록)
  const handleComboList = async (compCode) => {
    try {

      if (!compCode || compCode.trim() === '') {

        setSiteList([]);
        setFilterDeptList([]);
        setFilterPosList([]);
        return;
      }

      const response = await get("/user/comp/employee/comboList", {
        params: { compCode },
        headers: { 'Content-type': 'application/json' }
      });


      setSiteList(response.result?.siteList || []);
      setFilterDeptList(response.result?.deptList || []);
      setFilterPosList(response.result?.posList || []);

    } catch (error) {
      setSiteList([]);
      setFilterDeptList([]);
      setFilterPosList([]);
    }
  };

  useEffect(() => {
    if (compCode?.trim()) {
      handleComboList(compCode);
    } else {
      setSiteList([]);
      setFilterDeptList([]);
      setFilterPosList([]);
    }
  }, [compCode])

  useEffect(() => {
    refetch();
  }, [pageIndex, refetch]);

  // 사업장 선택 시 검색 조건만 업데이트 (부서/직위는 회사별로 이미 로드됨)
  const handleSiteChange = (e) => {
    const { value } = e.target;

    // 검색 조건의 사업장 값 업데이트
    methods.setValue("searchSite", value);

    // 부서/직위 검색 조건 초기화
    methods.setValue("searchDept", "");
    methods.setValue("searchPos", "");
  };

  const handleEmployee = (empCode) => {
    const filter = data?.resultList.filter((item) => empCode === item.empCode);
    if (filter.length > 0) {
      const selectedEmp = filter[0];

      setSelectEmployee({
        ...selectedEmp,
        isNew: false,
        isEdited: false
      });

      // 선택된 직원의 회사가 현재 compCode와 다르면 콤보 데이터 다시 로드
      if (!compCode && selectedEmp.compCode) {
        handleComboList(selectedEmp.compCode);
      }
    }
  }

  const handleChange = (e) => {
    const { name, value } = e.target;
    const cleanedValue = name === "phone" ? value.replace(/\D/g, "") : value;

    setSelectEmployee((prev) => ({
      ...prev,
      [name]: cleanedValue,
      isNew: prev.isNew,
      isEdited: true,
    }));
  }

  // 모달에서 선택한 회사 정보
  const handleModalSuccess = (selectedCompany) => {
    if (!selectedCompany) return;

    setSelectEmployee({
      ...initialEmployee,
      compCode: selectedCompany.compCode,
      compName: selectedCompany.compName,
      isNew: true,
      isEdited: false
    });
    setModalOpen(false);
  };

  // 추가
  const handleAdd = () => {
    if (!compCode && isAdmin) {
      toast.warn("회사를 먼저 선택하십시오.");
      setModalOpen(true);
      return;
    }
    const compName = isAdmin ? adminCompName : data?.resultList?.[0]?.compName || "";
    const defaultRoleId = assignableRoles.length > 0 ? assignableRoles[assignableRoles.length - 1].value : "R3";
    setSelectEmployee({
      ...initialEmployee,
      compCode: compCode,
      compName: compName,
      roleId: defaultRoleId,
      isNew: true,
      isEdited: false
    });
  }

  // 저장
  const handleSave = async () => {
    // 필수 입력사항 검증
    const missing = [];
    if (!selectEmployee.compCode) missing.push("회사");
    if (!selectEmployee.empName?.trim()) missing.push("이름");
    if (!selectEmployee.siteCode) missing.push("사업장");
    if (!selectEmployee.deptCode) missing.push("부서");
    if (!selectEmployee.posCode) missing.push("직위");
    if (missing.length > 0) {
      toast.warn(`필수 입력사항을 입력하십시오: ${missing.join(", ")}`);
      return;
    }

    const phone = selectEmployee.phone || "";
    if (phone && !/^\d{9,11}$/.test(phone)) {
      toast.warn("전화번호는 - 없이 숫자 9~11자리로 입력하세요.");
      return;
    }

    if (!selectEmployee.isNew && !selectEmployee.isEdited) {
      toast.info("저장할 사항이 없습니다.");
      return;
    }

    if (!window.confirm("저장 하시겠습니까?")) return;

    try {

      const empCode = selectEmployee.empCode;
      const url = selectEmployee.isNew ? "/user/comp/employee/register" : `/user/comp/employee/${empCode}`;

      // 저장할 데이터 준비 (안전직무가 빈 값이면 빈 문자열로 설정)
      const saveData = {
        ...selectEmployee,
        safeRole: selectEmployee.safeRole || "" // 빈 값이면 빈 문자열로 설정
      };

      const response = await post(url, saveData, {
        headers: {
          'Content-type': 'application/json'
        }
      });

      if (response.status !== false && response.result) {
        const { memberId, initialPassword } = response.result;
        toast.success(`${response.message} | 아이디: ${memberId} / 초기 비밀번호: ${initialPassword}`, { autoClose: 10000 });
      } else {
        toast.success(response.message || "등록 성공");
      }
      handlePageChange(pageIndex);
      refetch();

      if (selectEmployee.isNew) {
        setSelectEmployee({
          ...initialEmployee,
          compCode: compCode,
          compName: isAdmin ? adminCompName : companyData?.result?.compName || data?.resultList?.[0]?.compName || "",
          isNew: true,
          isEdited: false
        });

        setTimeout(() => {
          if (empNameInputRef.current) {
            empNameInputRef.current.focus();
          }
        }, 100);
      }

    } catch (error) {
      // axios interceptor가 toast로 에러 메시지를 이미 표시함
    }
  }

  // 계정 미생성 필터 적용된 목록
  const displayList = filterNoAccount
    ? (data?.resultList || []).filter(item => !item.memberId)
    : (data?.resultList || []);

  // 계정 일괄 생성
  const handleCreateBulkAccounts = async () => {
    if (selectedItems.length === 0) {
      toast.warn("계정을 생성할 사원을 선택하십시오.");
      return;
    }
    if (!window.confirm(`선택한 ${selectedItems.length}명의 사원에 대해 계정을 생성하시겠습니까?\n- 이미 계정이 있는 사원은 건너뜁니다.\n- 초기 비밀번호는 연락처 끝 4자리입니다.`)) return;

    try {
      const response = await post("/admin/employee/createBulkAccounts", { empCodes: selectedItems });
      if (response.result) {
        setBulkAccountModal({
          open: true,
          successCount: response.result.successCount || 0,
          failList: response.result.failList || [],
          results: response.result.results || [],
        });
        refetch();
      }
    } catch (error) {
      // axios interceptor가 toast로 에러 표시
    }
  };

  // 초기 비밀번호 목록 CSV 다운로드
  const handleDownloadPasswords = () => {
    const { results } = bulkAccountModal;
    if (!results.length) return;
    const header = "이름,계정ID,초기비밀번호";
    const rows = results.map(r => `${r.empName},${r.accountId},${r.initialPassword}`);
    const csv = [header, ...rows].join("\n");
    const blob = new Blob(["﻿" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "초기비밀번호_목록.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  if (isLoading) {
    return <div>로딩 중...</div>;
  }
  if (isError) {
    return <div>오류가 발생했습니다: {error?.message || "알 수 없는 에러"}</div>;
  }

  return (
    <FormProvider {...methods}>
      <form onSubmit={methods.handleSubmit(onSubmit)}>
        <div className="contents-wrap">
          <div className="page-title-wrap">
            <h2><span className="icon-dot-circle"></span>사원 관리</h2>
          </div>

          <div className="tab-cont-wrap">
            <div className="tab-cont-01 on">
              <div className="search-wrap">
                <div className="search-row">
                  <div className="search-item date-picker-box" style={{ overflow: "hidden" }}>
                    <label>회사</label>
                    {isAdmin ? (
                      <>
                        <Input name="compName" disabled value={adminCompName || ''} />
                        <button type="button" onClick={() => setModalOpen(true)} className="search-icon" />
                        {adminCompCode && (
                          <button
                            type="button"
                            className="btn-cancel"
                            onClick={() => { setAdminCompCode(""); setAdminCompName(""); }}
                          >
                            초기화
                          </button>
                        )}
                      </>
                    ) : (
                      <Input name="compName" disabled value={companyData?.result?.compName || data?.resultList?.[0]?.compName || ''} />
                    )}

                    <label style={{ marginLeft: "20px" }}>사업장</label>
                    <Select
                      name="searchSite"
                      className="input-search"
                      options={data?.siteList || []}
                      style={{ width: "150px" }}
                      defaultOption="전체"
                      valueKey="siteCode"
                      labelKey="siteName"
                      onChange={handleSiteChange}
                    />

                    <label style={{ marginLeft: "20px" }}>사원명</label>
                    <Input
                      name="searchName"
                      className="input-search"
                      style={{ width: "150px" }}
                      placeholder="사원명 검색"
                    />
                  </div>

                  <div className="search-btn-container">
                    <button className="btn-search">조회</button>
                  </div>
                </div>
              </div>

              <div className="row-1">
                <section className="wp75">
                  <div className="section-tit-wrap">
                    <h3 className="border-left-primary">
                      &nbsp;사원 목록
                      <span className="total-txt">
                        {" "} | 총 <span className="red">{data?.totalCount || 0}</span> 건
                      </span>
                    </h3>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '13px', color: '#555', cursor: 'pointer', whiteSpace: 'nowrap' }}>
                        <input
                          type="checkbox"
                          checked={filterNoAccount}
                          onChange={(e) => setFilterNoAccount(e.target.checked)}
                        />
                        계정 미생성만
                      </label>
                      {canWrite && (
                        <button type="button" className="btn-save" onClick={handleCreateBulkAccounts}>
                          계정 일괄 생성
                        </button>
                      )}
                      {canDelete && <button type="button" className="btn-delete" onClick={handleDelete}>삭제</button>}
                    </div>
                  </div>
                  <div className="table-container overflow-x-auto table-list-wrapper">
                    <table className="table-list" style={{ width: "100%" }}>
                      <colgroup>
                        <col style={{ width: "3%" }} />
                        <col style={{ width: "5%" }} />
                        <col style={{ width: "12%" }} />
                        <col style={{ width: "12%" }} />
                        <col style={{ width: "12%" }} />
                        <col style={{ width: "10%" }} />
                        <col style={{ width: "10%" }} />
                        <col style={{ width: "10%" }} />
                        <col style={{ width: "15%" }} />
                        <col style={{ width: "11%" }} />
                      </colgroup>
                      <thead>
                        <tr>
                          <th>
                            <input type="checkbox" checked={isAllChecked} onChange={handleSelectAll} />
                          </th>
                          <SortHeader field="DIVCODE" label="순번" />
                          <SortHeader field="DIVNAME" label="이름" style={{ textAlign: 'left' }} />
                          <th style={{ textAlign: 'left' }}>회사명</th>
                          <th style={{ textAlign: 'left' }}>사업장</th>
                          <th style={{ textAlign: 'left' }}>부서</th>
                          <th style={{ textAlign: 'left' }}>직위</th>
                          <th style={{ textAlign: 'left' }}>안전직무</th>
                          <th>전화번호</th>
                          <th style={{ textAlign: 'left' }}>비고</th>
                        </tr>
                      </thead>
                      <tbody>
                        {displayList.length > 0 ? (
                          displayList.map((item, idx) => (
                            <tr
                              key={item.empCode}
                              onClick={() => { handleEmployee(item.empCode); handleSelectItem(item.empCode) }}
                              style={{ cursor: "pointer" }}>
                              <td onClick={(e) => e.stopPropagation()}>
                                <input type="checkbox" checked={selectedItems.includes(item.empCode)} onChange={() => handleSelectItem(item.empCode)} />
                              </td>
                              <td>{(pageIndex - 1) * recordCountPerPage + idx + 1}</td>
                              <td style={{ textAlign: 'left' }}>{item.empName}</td>
                              <td style={{ textAlign: 'left' }}>{item.compName}</td>
                              <td style={{ textAlign: 'left' }}>{item.siteName}</td>
                              <td style={{ textAlign: 'left' }}>{item.deptName}</td>
                              <td style={{ textAlign: 'left' }}>{item.posName}</td>
                              <td style={{ textAlign: 'left' }}>{item.roleName}</td>
                              <td>{item.phone}</td>
                              <td style={{ textAlign: 'left' }}>{item.notice}</td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan={10} className="table-empty" style={{ textAlign: 'center' }}>
                              조회 결과가 없습니다.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginTop: '8px' }}>
                    <span style={{ fontSize: '14px', whiteSpace: 'nowrap', color: '#555' }}>페이지당 행 수</span>
                    <select
                      className="form-select form-select-sm"
                      style={{ width: 'auto' }}
                      value={recordCountPerPage}
                      onChange={(e) => changePageSize(Number(e.target.value))}
                    >
                      <option value={10}>10</option>
                      <option value={20}>20</option>
                      <option value={50}>50</option>
                      <option value={100}>100</option>
                    </select>
                  </div>

                  {data?.paginationInfo && data.paginationInfo.totalRecordCount > 0 && (
                    <Pagination
                      totalPages={Math.ceil(
                        data.paginationInfo.totalRecordCount / data.paginationInfo.recordCountPerPage
                      )}
                      currentPage={pageIndex}
                      onPageChange={handlePageChange}
                      pageRangeDisplayed={10}
                    />
                  )}
                </section>
                <section className="wp25">

                  <div className="section-tit-wrap">
                    <h3 className='border-left-primary'>&nbsp;사원 정보</h3>
                    <div className="btn-right-box">
                      <ul>
                        {canWrite && <li><button type="button" title="추가" className="btn-save" onClick={handleAdd}>추가</button></li>}
                        {canWrite && <li><button type="button" title="저장" className="btn-save" onClick={handleSave}>저장</button></li>}
                      </ul>
                    </div>
                  </div>

                  <table className="table-write">
                    <colgroup>
                      <col style={{ width: '30%' }} />
                      <col style={{ width: '70%' }} />
                    </colgroup>
                    <tbody id="tblBody1">
                      <tr>
                        <th>이름<span style={{ color: "red" }}>*</span></th>
                        <td>
                          <input
                            ref={empNameInputRef}
                            name="empName"
                            className="input-search"
                            style={{ width: '100%', maxWidth: 'none' }}
                            value={selectEmployee.empName || ""}
                            onChange={handleChange}
                          />
                        </td>
                      </tr>
                      <tr>
                        <th>사업장<span style={{ color: "red" }}>*</span></th>
                        <td>
                          <Select
                            name="siteCode"
                            className="input-search"
                            options={siteList}
                            value={selectEmployee.siteCode || ""}
                            style={{ width: "100%", maxWidth: "none" }}
                            defaultOption="선택"
                            valueKey="siteCode"
                            labelKey="siteName"
                            onChange={handleChange}
                          />
                        </td>
                      </tr>
                      <tr>
                        <th>부서<span style={{ color: "red" }}>*</span></th>
                        <td>
                          <Select
                            name="deptCode"
                            className="input-search"
                            options={filterDeptList}
                            value={selectEmployee.deptCode || ""}
                            style={{ width: "100%", maxWidth: "none" }}
                            defaultOption="선택"
                            valueKey="deptCode"
                            labelKey="deptName"
                            onChange={handleChange}
                          />
                        </td>
                      </tr>
                      <tr>
                        <th>직위<span style={{ color: "red" }}>*</span></th>
                        <td>
                          <Select
                            name="posCode"
                            className="input-search"
                            options={filterPosList}
                            value={selectEmployee.posCode || ""}
                            style={{ width: "100%", maxWidth: "none" }}
                            defaultOption="선택"
                            valueKey="posCode"
                            labelKey="posName"
                            onChange={handleChange}
                          />
                        </td>
                      </tr>
                      <tr>
                        <th>안전직무</th>
                        <td>
                          <Select
                            name="safeRole"
                            className="input-search"
                            options={data?.roleList || []}
                            value={selectEmployee.safeRole || ""}
                            style={{ width: '100%', maxWidth: 'none' }}
                            defaultOption="선택"
                            valueKey="safeRole"
                            labelKey="roleName"
                            onChange={handleChange}
                          />
                        </td>
                      </tr>
                      <tr>
                        <th>전화번호</th>
                        <td>
                          <input
                            name="phone"
                            className="input-search"
                            style={{ width: '100%', maxWidth: 'none' }}
                            placeholder="- 없이 숫자만"
                            maxLength={11}
                            value={selectEmployee.phone || ""}
                            onChange={handleChange}
                            onPaste={(e) => {
                              e.preventDefault();
                              const text = e.clipboardData.getData("text/plain") || "";
                              const onlyDigits = text.replace(/\D/g, "").slice(0, 11);
                              setSelectEmployee((prev) => ({ ...prev, phone: onlyDigits, isEdited: true }));
                            }}
                          />
                          <p style={{ fontSize: '11px', color: '#888', margin: '3px 0 0' }}>- 없이 숫자만 입력하세요.</p>
                        </td>
                      </tr>
                      <tr>
                        <th>비고</th>
                        <td>
                          <input
                            name="notice"
                            className="input-search"
                            style={{ width: '100%', maxWidth: 'none' }}
                            value={selectEmployee.notice || ""}
                            onChange={handleChange}
                          />
                        </td>
                      </tr>
                      <tr>
                        <th>이메일</th>
                        <td>
                          <input
                            name="email"
                            type="email"
                            className="input-search"
                            style={{ width: '100%', maxWidth: 'none' }}
                            placeholder="이메일 주소"
                            value={selectEmployee.email || ""}
                            onChange={handleChange}
                          />
                        </td>
                      </tr>
                      <tr>
                        <th>권한</th>
                        <td>
                          {assignableRoles.length > 0 ? (
                            <Select
                              name="roleId"
                              className="input-search"
                              options={assignableRoles}
                              value={selectEmployee.roleId || ""}
                              style={{ width: '100%', maxWidth: 'none' }}
                              defaultOption="선택"
                              valueKey="value"
                              labelKey="label"
                              onChange={handleChange}
                            />
                          ) : (
                            <input
                              type="text"
                              className="input-search"
                              style={{ width: '100%', maxWidth: 'none', backgroundColor: '#f8f9fa' }}
                              value={selectEmployee.roleId || ""}
                              readOnly
                            />
                          )}
                        </td>
                      </tr>
                      <tr>
                        <th style={{ backgroundColor: '#f0f4f8' }}>계정 ID</th>
                        <td>
                          <input
                            type="text"
                            className="input-search"
                            style={{ width: '100%', maxWidth: 'none', backgroundColor: '#f8f9fa' }}
                            value={selectEmployee.memberId || "(자동 생성)"}
                            readOnly
                          />
                        </td>
                      </tr>
                      {!selectEmployee.isNew && (
                        <tr>
                          <th style={{ backgroundColor: '#f0f4f8' }}>초기 비밀번호</th>
                          <td>
                            <input
                              type="text"
                              className="input-search"
                              style={{ width: '100%', maxWidth: 'none', backgroundColor: '#f8f9fa' }}
                              value={selectEmployee.initialPassword || (selectEmployee.memberId ? "********" : "(자동 생성)")}
                              readOnly
                            />
                            {selectEmployee.initialPassword && (
                              <p style={{ fontSize: '0.8rem', color: '#666', marginTop: '4px' }}>
                                * 방금 생성된 초기 비밀번호입니다. 사원에게 전달해 주세요.
                              </p>
                            )}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </section>
              </div>
            </div>
          </div>
        </div>
      </form>
      {isAdmin && modalOpen && (
        <CompanySearchModal
          isOpen={modalOpen}
          onClose={() => setModalOpen(false)}
          onConfirm={handleModalSuccess}
        />
      )}

      {bulkAccountModal.open && (
        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ backgroundColor: '#fff', borderRadius: '8px', padding: '28px', minWidth: '420px', maxWidth: '560px', boxShadow: '0 4px 20px rgba(0,0,0,0.2)' }}>
            <h3 style={{ margin: '0 0 16px', fontSize: '16px', fontWeight: 600 }}>계정 일괄 생성 결과</h3>

            <p style={{ margin: '0 0 8px', fontSize: '14px' }}>
              생성 완료: <strong style={{ color: '#1e6fd9' }}>{bulkAccountModal.successCount}명</strong>
            </p>

            {bulkAccountModal.failList.length > 0 && (
              <div style={{ margin: '0 0 12px', padding: '10px 12px', backgroundColor: '#fff8e1', borderRadius: '4px', border: '1px solid #ffe082' }}>
                <p style={{ margin: '0 0 4px', fontSize: '13px', fontWeight: 600, color: '#e65100' }}>
                  계정 미생성 ({bulkAccountModal.failList.length}명) — 전화번호 없음
                </p>
                <p style={{ margin: 0, fontSize: '13px', color: '#555' }}>
                  {bulkAccountModal.failList.join(', ')}
                </p>
              </div>
            )}

            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', marginTop: '20px' }}>
              {bulkAccountModal.results.length > 0 && (
                <button
                  type="button"
                  className="btn-save"
                  onClick={handleDownloadPasswords}
                >
                  초기 비밀번호 다운로드
                </button>
              )}
              <button
                type="button"
                className="btn-delete"
                onClick={() => setBulkAccountModal({ open: false, successCount: 0, failList: [], results: [] })}
              >
                닫기
              </button>
            </div>
          </div>
        </div>
      )}

    </FormProvider>
  );
};

export { Employee };